import { useId, useState, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import { TextField } from './fields';

export interface PickerOption<V> {
  value: V;
  label: string;
  detail?: string;
}

export interface PickerProps<V> {
  open: boolean;
  title: string;
  options: ReadonlyArray<PickerOption<V>>;
  value: V;
  onPick: (value: V) => void;
  onClose: () => void;
  closeLabel: string;
  /** Shown above the list when there are more than six options. */
  searchLabel?: string;
  searchPlaceholder?: string;
  /** Copy for no match, given the trimmed query. */
  noMatch?: (query: string) => string;
}

/**
 * Full-screen picker, never an inline dropdown (design system, SelectField). Adds a search
 * field when the list is long, because the grower, farm and variety lists run to dozens.
 */
export function Picker<V>(props: PickerProps<V>) {
  return props.open ? <PickerBody {...props} /> : null;
}

function PickerBody<V>({
  title,
  options,
  value,
  onPick,
  onClose,
  closeLabel,
  searchLabel,
  searchPlaceholder,
  noMatch,
}: PickerProps<V>) {
  const [query, setQuery] = useState('');

  const searchable = options.length > 6 && Boolean(searchLabel);
  const q = query.trim().toLowerCase();
  const shown = q
    ? options.filter((o) => `${o.label} ${o.detail ?? ''}`.toLowerCase().includes(q))
    : options;

  return (
    <div role="dialog" aria-modal="true" aria-label={title} className="fw-picker">
      <div className="fw-picker__header">
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className="fw-reset-button fw-picker__close"
        >
          <Icon name="close" size={24} />
        </button>
        <span className="fw-body-medium">{title}</span>
      </div>
      {searchable && (
        <div className="fw-picker__search">
          <TextField
            label={searchLabel}
            value={query}
            onChange={setQuery}
            placeholder={searchPlaceholder}
            inputMode="search"
            autoComplete="off"
          />
        </div>
      )}
      <div className="fw-picker__list">
        {shown.map((o, i) => (
          <button
            key={`${String(o.value)}-${i}`}
            type="button"
            className="fw-picker__option"
            aria-pressed={o.value === value}
            onClick={() => onPick(o.value)}
          >
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="fw-picker__label">{o.label}</span>
              {o.detail && <span className="fw-secondary fw-tnum">{o.detail}</span>}
            </span>
            {o.value === value && <Icon name="checkmark" size={22} color="var(--color-primary)" />}
          </button>
        ))}
        {q && shown.length === 0 && noMatch && (
          <div
            className="fw-secondary"
            style={{ padding: 'var(--space-6) var(--space-4)', textAlign: 'center' }}
          >
            {noMatch(query.trim())}
          </div>
        )}
      </div>
    </div>
  );
}

export interface PickerFieldProps {
  label: ReactNode;
  /** The chosen option's text, or undefined for the placeholder. */
  value?: string;
  placeholder: string;
  onOpen: () => void;
  required?: boolean;
  error?: string;
  disabled?: boolean;
}

/** A field that looks like the design's SelectField and opens a Picker. */
export function PickerField({
  label,
  value,
  placeholder,
  onOpen,
  required,
  error,
  disabled,
}: PickerFieldProps) {
  const id = useId();
  return (
    <div className="fw-field">
      <span id={`${id}-label`} className="fw-field__label">
        {label}
        {required ? ' *' : ''}
      </span>
      <button
        type="button"
        onClick={onOpen}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-labelledby={`${id}-label ${id}-value`}
        className={`fw-field__shell fw-picker-field${error ? ' fw-field__shell--invalid' : ''}${disabled ? ' fw-field__shell--disabled' : ''}`}
      >
        <span
          id={`${id}-value`}
          className={`fw-picker-field__value${value ? '' : ' fw-picker-field__value--empty'}`}
        >
          {value ?? placeholder}
        </span>
        <Icon name="chevron-down" size={20} color="var(--text-muted)" />
      </button>
      {error && (
        <div className="fw-field__message fw-field__message--invalid" role="alert">
          <Icon name="alert-circle" size={16} style={{ marginTop: '2px' }} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

export interface FilterRow {
  key: string;
  icon: IconName;
  label: string;
  value: string;
  /** True when a value is chosen; the "All …" default reads muted. */
  active: boolean;
  onClick: () => void;
}

/** Stacked filter rows in one card: label on the left, current value and chevron on the right. */
export function FilterCard({ rows }: { rows: ReadonlyArray<FilterRow> }) {
  return (
    <div className="fw-rows">
      {rows.map((r) => (
        <button key={r.key} type="button" className="fw-filter-row" onClick={r.onClick}>
          <Icon name={r.icon} size={24} color="var(--text-muted)" />
          <span style={{ flex: 'none' }}>{r.label}</span>
          <span className={`fw-filter-row__value${r.active ? '' : ' fw-filter-row__value--muted'}`}>
            {r.value}
          </span>
          <Icon name="chevron-forward" size={20} className="fw-row__chevron" />
        </button>
      ))}
    </div>
  );
}

export interface ChoiceSheetOption {
  key: string;
  icon: IconName;
  title: string;
  hint: string;
  onPick: () => void;
}

/** Bottom sheet with a short list of ways to do something (Import from Excel, Add manually). */
export function ChoiceSheet({
  open,
  title,
  hint,
  options,
  onClose,
  closeLabel,
}: {
  open: boolean;
  title: string;
  hint: string;
  options: ReadonlyArray<ChoiceSheetOption>;
  onClose: () => void;
  closeLabel: string;
}) {
  if (!open) return null;
  return (
    <div className="fw-scrim fw-scrim--bottom" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="fw-sheet"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="fw-sheet__head">
          <span className="fw-title">{title}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className="fw-reset-button fw-picker__close"
          >
            <Icon name="close" size={24} color="var(--text-muted)" />
          </button>
        </div>
        <span style={{ font: 'var(--type-body)', color: 'var(--text-muted)' }}>{hint}</span>
        <div className="fw-rows">
          {options.map((o) => (
            <button
              key={o.key}
              type="button"
              className="fw-row fw-row--tappable"
              onClick={o.onPick}
            >
              <span className="fw-row__leading">
                <Icon name={o.icon} size={24} color="var(--color-primary)" />
              </span>
              <span className="fw-row__main">
                <span className="fw-row__title">{o.title}</span>
                <span className="fw-row__secondary">{o.hint}</span>
              </span>
              <Icon name="chevron-forward" size={20} className="fw-row__chevron" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
